'use client';

import { useRouter } from 'next/navigation';
import { useState, useTransition } from 'react';
import z from 'zod';

const CredentialsInput = z.object({
  email: z.email('Invalid email address'),
  password: z
    .string()
    .min(8, { message: 'Password must be at least 8 characters long' })
    .max(20, { message: 'Password cannot exceed 20 characters' })
    .regex(/[0-9]/, { message: 'Must contain at least one number' })
    .regex(/[!@#$%^&*]/, {
      message: 'Must contain at least one special character',
    }),
});

export function useAuth() {
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  async function signIn(
    type: string,
    data: unknown,
    redirectUrl?: string
  ): Promise<void> {
    setError(null);

    startTransition(async () => {
      // OIDC
      if (type !== 'credentials') {
        const queryParams = new URLSearchParams();
        if (redirectUrl) queryParams.set('redirect_url', redirectUrl);
        router.push(`/api/auth/login/${type}?${queryParams}`);
        return;
      }

      // Credentials
      try {
        const input = CredentialsInput.parse(data);
        const response = await fetch('/api/auth/login/credentials', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });

        if (!response.ok) {
          setError(`Failed to login ${response.status}`);
          return;
        }

        const result = await response.json();

        if (!result.success) {
          setError(result.error);
          return;
        }

        router.push(redirectUrl ?? '/');
      } catch (error) {
        if (error instanceof z.ZodError) {
          setError(error.issues.map(i => i.message).join(', '));
        } else if (error instanceof Error) {
          setError(error.message);
        } else {
          setError('An unknown error occurred');
        }
      }
    });
  }

  async function signOut() {
    startTransition(async () => {
      await fetch('/api/auth/logout');
      router.push('/login');
    });
  }

  return {
    signIn,
    signOut,
    isLoading: isPending,
    error,
  };
}
